/**
 * Base PDF Generator - Shared functionality for all dashboard PDF exports
 * Provides common methods for page management, styling, charts, and tables
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Shared color palette
export const COLORS = {
  primary: '#1A4D2E',
  primaryLight: '#22633A',
  secondary: '#15803D',
  accent: '#10B981',
  text: '#1C1917',
  textMuted: '#78716C',
  textLight: '#A8A29E',
  border: '#D6D3D1',
  borderLight: '#E7E5E4',
  background: '#FFFFFF',
  backgroundAlt: '#FAFAF9',
  // Section colors
  environmental: '#15803D',
  emissions: '#059669',
  energy: '#F59E0B',
  water: '#0284C7',
  waste: '#78716C',
  social: '#7C3AED',
  governance: '#4F46E5',
  ghg: '#059669',
  // Status colors
  improved: '#10B981',
  declined: '#EF4444',
  stable: '#78716C',
  attention: '#F59E0B',
  achievement: '#059669',
};

// Page dimensions (A4 in mm)
export const PAGE = {
  width: 210,
  height: 297,
  margin: 15,
  marginSmall: 12,
  contentWidth: 180,
  headerHeight: 14,
  footerHeight: 12,
};

// Industry benchmarks
export const BENCHMARKS = {
  renewableEnergy: { good: 50, excellent: 80 },
  wasteRecovery: { good: 50, excellent: 70 },
  waterRecycle: { good: 30, excellent: 50 },
  apDays: { excellent: 30, good: 60, fair: 90, typical: '30-90' },
  ltifr: { excellent: 1, good: 2, fair: 5 },
  turnover: { excellent: 5, good: 10, fair: 20 },
  diversity: { good: 30, excellent: 40 },
};

/**
 * Base PDF Generator Class
 */
export class BasePDFGenerator {
  constructor(options = {}) {
    this.doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    
    this.organization = options.organization || {};
    this.dateRange = options.dateRange || {};
    this.facilities = options.facilities || [];
    this.user = options.user || {};
    
    this.pageNumber = 0;
    this.totalPages = 0;
    this.currentY = PAGE.margin + PAGE.headerHeight;
    this.generatedDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    this.generatedTimestamp = new Date().toISOString();
    
    // To be overridden by subclasses
    this.reportTitle = 'Dashboard Report';
    this.reportSubtitle = 'Performance Analysis';
    this.themeColor = COLORS.primary;
    this.reportVersion = '1.0';
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  addNewPage() {
    // Add footer to current page before creating new page (except for first page)
    if (this.pageNumber > 0) {
      this.addFooter();
      this.doc.addPage();
    }
    this.pageNumber++;
    this.currentY = PAGE.margin + PAGE.headerHeight;
    if (this.pageNumber > 1) {
      this.addHeader();
    }
  }

  addHeader() {
    const y = 8;
    
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(8);
    this.doc.setTextColor(this.themeColor);
    this.doc.text(this.organization.name || 'Organization', PAGE.margin, y);
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(8);
    this.doc.setTextColor(COLORS.textMuted);
    this.doc.text(this.reportTitle, PAGE.width / 2, y, { align: 'center' });
    
    const period = this.getReportingPeriod();
    this.doc.text(period, PAGE.width - PAGE.margin, y, { align: 'right' });
    
    this.doc.setDrawColor(COLORS.borderLight);
    this.doc.setLineWidth(0.3);
    this.doc.line(PAGE.margin, y + 4, PAGE.width - PAGE.margin, y + 4);
  }

  addFooter() {
    const y = PAGE.height - 8;
    
    this.doc.setDrawColor(COLORS.borderLight);
    this.doc.setLineWidth(0.3);
    this.doc.line(PAGE.margin, y - 6, PAGE.width - PAGE.margin, y - 6);
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(6);
    this.doc.setTextColor(COLORS.textMuted);
    
    this.doc.text('www.sustainrepo.com', PAGE.margin, y - 2);
    this.doc.setFont('helvetica', 'italic');
    this.doc.text('Confidential', PAGE.width / 2, y - 2, { align: 'center' });
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`Page ${this.pageNumber}`, PAGE.width - PAGE.margin, y - 2, { align: 'right' });
    
    this.doc.setFontSize(5);
    this.doc.text('info@sustainrepo.com', PAGE.margin, y + 2);
    this.doc.text('Generated by SustainRepo', PAGE.width - PAGE.margin, y + 2, { align: 'right' });
  }

  checkPageBreak(requiredHeight) {
    // Ensure minimum 20mm gap before footer area
    const footerBuffer = 20;
    const availableHeight = PAGE.height - PAGE.footerHeight - footerBuffer - this.currentY;
    if (requiredHeight > availableHeight) {
      this.addNewPage();
      return true;
    }
    return false;
  }

  getReportingPeriod() {
    if (this.dateRange.from && this.dateRange.to) {
      const from = new Date(this.dateRange.from);
      const to = new Date(this.dateRange.to);
      return `${from.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${to.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
    }
    return 'Current Period';
  }

  getCO2Unit() {
    return 'tCO2e';
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COVER PAGE
  // ═══════════════════════════════════════════════════════════════════════════

  addCoverPage() {
    this.pageNumber = 1;
    const centerX = PAGE.width / 2;
    
    // Background
    this.doc.setFillColor('#F0FDF4');
    this.doc.rect(0, 0, PAGE.width, PAGE.height, 'F');
    
    // Decorative elements
    this.doc.setFillColor('#DCFCE7');
    this.doc.circle(30, 50, 40, 'F');
    this.doc.circle(180, 250, 50, 'F');
    this.doc.setFillColor('#D1FAE5');
    this.doc.circle(170, 80, 25, 'F');
    
    // Top banner
    this.doc.setFillColor(this.themeColor);
    this.doc.rect(0, 0, PAGE.width, 50, 'F');
    
    // Accent line
    this.doc.setFillColor(COLORS.accent);
    this.doc.rect(0, 50, PAGE.width, 4, 'F');
    
    // Company name
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(16);
    this.doc.setTextColor('#FFFFFF');
    this.doc.text(this.organization.name || 'Organization', centerX, 28, { align: 'center' });
    
    // Theme icon circle
    this.doc.setFillColor('#FFFFFF');
    this.doc.circle(centerX, 75, 15, 'F');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(18);
    this.doc.setTextColor(this.themeColor);
    this.doc.text(this.getReportIcon(), centerX, 80, { align: 'center' });
    
    // Main title
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(32);
    this.doc.setTextColor(this.themeColor);
    this.doc.text(this.reportTitle, centerX, 115, { align: 'center' });
    
    // Subtitle
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(14);
    this.doc.setTextColor(COLORS.textMuted);
    this.doc.text(this.reportSubtitle, centerX, 130, { align: 'center' });
    
    // Tagline
    this.doc.setFont('helvetica', 'italic');
    this.doc.setFontSize(10);
    this.doc.text('"Driving Sustainable Performance Through Data"', centerX, 145, { align: 'center' });
    
    // Decorative line
    this.doc.setDrawColor(COLORS.accent);
    this.doc.setLineWidth(2);
    this.doc.line(centerX - 50, 155, centerX + 50, 155);
    
    // Metadata cards
    const cardY = 175;
    const cardWidth = 80;
    const cardHeight = 40;
    
    this.drawMetadataCard(PAGE.margin + 5, cardY, cardWidth, cardHeight, 'REPORTING PERIOD', this.getReportingPeriod());
    this.drawMetadataCard(PAGE.width - PAGE.margin - cardWidth - 5, cardY, cardWidth, cardHeight, 'GENERATED ON', this.generatedDate);
    
    // Bottom info
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    this.doc.setTextColor(COLORS.textMuted);
    this.doc.text('Prepared by SustainRepo', centerX, 240, { align: 'center' });
    this.doc.setFontSize(8);
    this.doc.text(`Report Version ${this.reportVersion}`, centerX, 248, { align: 'center' });
    
    // Footer bar
    this.doc.setFillColor(this.themeColor);
    this.doc.rect(0, PAGE.height - 20, PAGE.width, 20, 'F');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(9);
    this.doc.setTextColor('#FFFFFF');
    this.doc.text('CONFIDENTIAL', centerX, PAGE.height - 10, { align: 'center' });
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(7);
    this.doc.text('www.sustainrepo.com  |  info@sustainrepo.com', centerX, PAGE.height - 5, { align: 'center' });
  }

  getReportIcon() {
    return 'R'; // Override in subclasses
  }

  drawMetadataCard(x, y, width, height, label, value) {
    this.doc.setFillColor('#FFFFFF');
    this.doc.setDrawColor(COLORS.border);
    this.doc.setLineWidth(0.3);
    this.doc.roundedRect(x, y, width, height, 3, 3, 'FD');
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(7);
    this.doc.setTextColor(COLORS.textMuted);
    this.doc.text(label, x + width/2, y + 10, { align: 'center' });
    
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(10);
    this.doc.setTextColor(COLORS.text);
    this.doc.text(value, x + width/2, y + 25, { align: 'center' });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  addPageTitle(title, color = null) {
    const useColor = color || this.themeColor;
    
    if (color) {
      this.doc.setFillColor(useColor);
      this.doc.roundedRect(PAGE.margin, this.currentY - 4, 8, 8, 2, 2, 'F');
    }
    
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(18);
    this.doc.setTextColor(useColor);
    this.doc.text(title, color ? PAGE.margin + 12 : PAGE.margin, this.currentY);
    
    this.doc.setDrawColor(useColor);
    this.doc.setLineWidth(0.8);
    const titleWidth = this.doc.getTextWidth(title);
    this.doc.line(color ? PAGE.margin + 12 : PAGE.margin, this.currentY + 2, (color ? PAGE.margin + 12 : PAGE.margin) + titleWidth, this.currentY + 2);
    
    this.currentY += 15;
  }

  addSubsectionTitle(title) {
    this.checkPageBreak(15);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(11);
    this.doc.setTextColor(COLORS.text);
    this.doc.text(title, PAGE.margin, this.currentY);
    this.currentY += 8;
  }

  addAnalysisBox(text, color = null) {
    if (!text) return;
    
    const useColor = color || COLORS.accent;
    
    // Calculate text width with safety margin for font rendering variance
    const textWidth = PAGE.contentWidth - 30;
    const textStartX = PAGE.margin + 4 + 10;
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    
    const lines = this.doc.splitTextToSize(text, textWidth);
    const lineHeight = 5;
    const boxHeight = Math.max(lines.length * lineHeight + 22, 34);
    
    this.checkPageBreak(boxHeight + 5);
    
    this.doc.setFillColor('#F0FDF4');
    this.doc.setDrawColor(useColor);
    this.doc.setLineWidth(0.5);
    
    this.doc.roundedRect(PAGE.margin, this.currentY, PAGE.contentWidth, boxHeight, 3, 3, 'FD');
    
    // Left accent bar
    this.doc.setFillColor(useColor);
    this.doc.rect(PAGE.margin, this.currentY, 4, boxHeight, 'F');
    
    // "ANALYSIS" label
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(8);
    this.doc.setTextColor(this.themeColor);
    this.doc.text('ANALYSIS', textStartX, this.currentY + 8, { charSpace: 0 });
    
    // Analysis text
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    this.doc.setTextColor(COLORS.text);
    
    let textY = this.currentY + 16;
    lines.forEach((line) => {
      this.doc.text(line, textStartX, textY, { charSpace: 0 });
      textY += lineHeight;
    });
    
    this.currentY += boxHeight + 10;
  }

  addFullWidthTable(data, headerColor = null) {
    const useColor = headerColor || this.themeColor;
    const rowHeight = 10;
    const startX = PAGE.margin;
    const numCols = data[0].length;
    const colWidth = PAGE.contentWidth / numCols;
    
    // Calculate total table height and check if it fits on current page
    const totalTableHeight = data.length * rowHeight;
    this.checkPageBreak(totalTableHeight + 15);
    
    let y = this.currentY;
    
    data.forEach((row, rowIndex) => {
      let x = startX;
      const isHeader = rowIndex === 0;
      const isTotal = row[0]?.toString().toLowerCase().includes('total');
      
      if (isHeader) {
        this.doc.setFillColor(useColor);
      } else if (isTotal) {
        this.doc.setFillColor('#E7E5E4');
      } else {
        this.doc.setFillColor(rowIndex % 2 === 0 ? COLORS.backgroundAlt : '#FFFFFF');
      }
      
      this.doc.rect(x, y, PAGE.contentWidth, rowHeight, 'F');
      
      row.forEach((cell, colIndex) => {
        this.doc.setFont('helvetica', isHeader || isTotal ? 'bold' : 'normal');
        this.doc.setFontSize(9);
        this.doc.setTextColor(isHeader ? '#FFFFFF' : COLORS.text);
        
        const cellText = String(cell || '');
        
        if (colIndex === 0) {
          this.doc.text(cellText, x + 5, y + 7);
        } else {
          this.doc.text(cellText, x + colWidth / 2, y + 7, { align: 'center' });
        }
        x += colWidth;
      });
      
      y += rowHeight;
    });
    
    this.doc.setDrawColor(COLORS.border);
    this.doc.setLineWidth(0.3);
    this.doc.rect(startX, this.currentY, PAGE.contentWidth, data.length * rowHeight);
    
    // Add adequate spacing after table to prevent footer collision
    this.currentY = y + 15;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CHART CAPTURE
  // ═══════════════════════════════════════════════════════════════════════════

  async addChartFromRef(testId, title, maxHeight = 70) {
    const chartElement = document.querySelector(`[data-testid="${testId}"]`);
    
    if (chartElement) {
      try {
        // ═══════════════════════════════════════════════════════════════════
        // STEP 1: Store original styles and find all parents with overflow-hidden
        // ═══════════════════════════════════════════════════════════════════
        const originalElementStyles = {
          width: chartElement.style.width,
          minWidth: chartElement.style.minWidth,
          maxWidth: chartElement.style.maxWidth,
          overflow: chartElement.style.overflow,
          position: chartElement.style.position,
        };
        
        // Find and temporarily fix all parent elements with overflow-hidden
        const parentsToRestore = [];
        let parent = chartElement.parentElement;
        while (parent && parent !== document.body) {
          const computedStyle = window.getComputedStyle(parent);
          if (computedStyle.overflow === 'hidden' || computedStyle.overflowX === 'hidden') {
            parentsToRestore.push({
              element: parent,
              originalOverflow: parent.style.overflow,
              originalOverflowX: parent.style.overflowX,
              originalWidth: parent.style.width,
              originalMinWidth: parent.style.minWidth,
              originalMaxWidth: parent.style.maxWidth,
            });
          }
          parent = parent.parentElement;
        }
        
        // ═══════════════════════════════════════════════════════════════════
        // STEP 2: Apply export-friendly styles (full width, visible overflow)
        // ═══════════════════════════════════════════════════════════════════
        
        // Set overflow visible on all parent containers
        parentsToRestore.forEach(({ element }) => {
          element.style.overflow = 'visible';
          element.style.overflowX = 'visible';
        });
        
        // Expand chart element to full width for better capture
        // Use 900px as a standard export width that shows all data points
        const exportWidth = 900;
        chartElement.style.width = `${exportWidth}px`;
        chartElement.style.minWidth = `${exportWidth}px`;
        chartElement.style.maxWidth = 'none';
        chartElement.style.overflow = 'visible';
        
        // Also expand immediate parent (SectionCard content area) if it exists
        const immediateParent = chartElement.closest('[data-testid]') || chartElement.parentElement;
        let immediateParentOriginalStyles = null;
        if (immediateParent && immediateParent !== chartElement) {
          immediateParentOriginalStyles = {
            width: immediateParent.style.width,
            minWidth: immediateParent.style.minWidth,
            maxWidth: immediateParent.style.maxWidth,
            overflow: immediateParent.style.overflow,
          };
          immediateParent.style.width = `${exportWidth}px`;
          immediateParent.style.minWidth = `${exportWidth}px`;
          immediateParent.style.maxWidth = 'none';
          immediateParent.style.overflow = 'visible';
        }
        
        // ═══════════════════════════════════════════════════════════════════
        // STEP 3: Wait for Recharts ResponsiveContainer to detect resize and re-render
        // ═══════════════════════════════════════════════════════════════════
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Trigger a resize event to ensure ResponsiveContainer updates
        window.dispatchEvent(new Event('resize'));
        
        // Wait again for the chart to finish re-rendering
        await new Promise(resolve => setTimeout(resolve, 400));
        
        // ═══════════════════════════════════════════════════════════════════
        // STEP 4: Hide UI elements and capture
        // ═══════════════════════════════════════════════════════════════════
        const buttons = chartElement.querySelectorAll('button');
        const originalVisibility = [];
        buttons.forEach((btn, i) => {
          originalVisibility[i] = btn.style.visibility;
          btn.style.visibility = 'hidden';
        });
        
        // Get actual rendered dimensions after resize
        const rect = chartElement.getBoundingClientRect();
        const captureWidth = Math.max(rect.width, exportWidth);
        const captureHeight = Math.max(rect.height, chartElement.scrollHeight);
        
        const capturePromise = html2canvas(chartElement, {
          scale: 2,
          backgroundColor: '#FFFFFF',
          logging: false,
          useCORS: true,
          allowTaint: true,
          foreignObjectRendering: false,
          removeContainer: true,
          width: captureWidth,
          height: captureHeight,
          windowWidth: captureWidth + 100,
          windowHeight: captureHeight + 100,
          scrollX: 0,
          scrollY: 0,
          x: 0,
          y: 0,
        });
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 15000)
        );
        
        const canvas = await Promise.race([capturePromise, timeoutPromise]);
        
        // ═══════════════════════════════════════════════════════════════════
        // STEP 5: Restore all original styles
        // ═══════════════════════════════════════════════════════════════════
        buttons.forEach((btn, i) => {
          btn.style.visibility = originalVisibility[i] || 'visible';
        });
        
        // Restore chart element styles
        chartElement.style.width = originalElementStyles.width;
        chartElement.style.minWidth = originalElementStyles.minWidth;
        chartElement.style.maxWidth = originalElementStyles.maxWidth;
        chartElement.style.overflow = originalElementStyles.overflow;
        chartElement.style.position = originalElementStyles.position;
        
        // Restore immediate parent styles
        if (immediateParent && immediateParentOriginalStyles) {
          immediateParent.style.width = immediateParentOriginalStyles.width;
          immediateParent.style.minWidth = immediateParentOriginalStyles.minWidth;
          immediateParent.style.maxWidth = immediateParentOriginalStyles.maxWidth;
          immediateParent.style.overflow = immediateParentOriginalStyles.overflow;
        }
        
        // Restore all parent containers
        parentsToRestore.forEach(({ element, originalOverflow, originalOverflowX, originalWidth, originalMinWidth, originalMaxWidth }) => {
          element.style.overflow = originalOverflow;
          element.style.overflowX = originalOverflowX;
          element.style.width = originalWidth;
          element.style.minWidth = originalMinWidth;
          element.style.maxWidth = originalMaxWidth;
        });
        
        // Trigger resize to restore original chart rendering
        window.dispatchEvent(new Event('resize'));
        
        // ═══════════════════════════════════════════════════════════════════
        // STEP 6: Add captured image to PDF
        // ═══════════════════════════════════════════════════════════════════
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const aspectRatio = canvas.width / canvas.height;
        
        // Start with full content width
        let imgWidth = PAGE.contentWidth;
        let imgHeight = imgWidth / aspectRatio;
        
        // If height exceeds max, scale down BOTH dimensions proportionally
        if (imgHeight > maxHeight) {
          imgHeight = maxHeight;
          imgWidth = imgHeight * aspectRatio;
        }
        
        this.checkPageBreak(imgHeight + 15);
        
        // Center the image horizontally
        const chartX = PAGE.margin + (PAGE.contentWidth - imgWidth) / 2;
        
        // Draw border around full content width area
        this.doc.setDrawColor(COLORS.borderLight);
        this.doc.setLineWidth(0.3);
        this.doc.roundedRect(PAGE.margin, this.currentY, PAGE.contentWidth, imgHeight + 6, 2, 2, 'S');
        
        // Add image centered within the border
        this.doc.addImage(imgData, 'JPEG', chartX, this.currentY + 3, imgWidth, imgHeight);
        this.currentY += imgHeight + 12;
        
        return true;
      } catch (error) {
        console.warn(`Chart capture failed: ${testId}`, error);
        // Attempt to restore styles even on error
        try {
          const buttons = chartElement.querySelectorAll('button');
          buttons.forEach(btn => { btn.style.visibility = 'visible'; });
          window.dispatchEvent(new Event('resize'));
        } catch (e) { /* ignore */ }
        this.addChartPlaceholder(title, maxHeight * 0.6);
        return false;
      }
    } else {
      this.addChartPlaceholder(title, maxHeight * 0.6);
      return false;
    }
  }

  addChartPlaceholder(title, height = 50) {
    this.checkPageBreak(height + 5);
    
    this.doc.setFillColor(COLORS.backgroundAlt);
    this.doc.setDrawColor(COLORS.border);
    this.doc.setLineWidth(0.3);
    this.doc.roundedRect(PAGE.margin, this.currentY, PAGE.contentWidth, height, 2, 2, 'FD');
    
    this.doc.setFont('helvetica', 'italic');
    this.doc.setFontSize(9);
    this.doc.setTextColor(COLORS.textMuted);
    this.doc.text(`[${title}]`, PAGE.width / 2, this.currentY + height / 2 - 3, { align: 'center' });
    this.doc.setFontSize(7);
    this.doc.text('Chart will appear when viewing the dashboard', PAGE.width / 2, this.currentY + height / 2 + 4, { align: 'center' });
    
    this.currentY += height + 5;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // KPI CARDS
  // ═══════════════════════════════════════════════════════════════════════════

  addKPIGrid(kpis, cols = 3) {
    const cardWidth = (PAGE.contentWidth - (cols - 1) * 8) / cols;
    const cardHeight = 35;
    let x = PAGE.margin;
    let y = this.currentY;
    
    kpis.forEach((kpi, index) => {
      if (index > 0 && index % cols === 0) {
        x = PAGE.margin;
        y += cardHeight + 8;
      }
      
      this.drawKPICard(x, y, cardWidth, cardHeight, kpi);
      x += cardWidth + 8;
    });
    
    const totalRows = Math.ceil(kpis.length / cols);
    this.currentY = y + cardHeight + 10;
  }

  drawKPICard(x, y, width, height, kpi) {
    this.doc.setFillColor('#FFFFFF');
    this.doc.setDrawColor(COLORS.border);
    this.doc.setLineWidth(0.3);
    this.doc.roundedRect(x, y, width, height, 2, 2, 'FD');
    
    this.doc.setFillColor(kpi.color || this.themeColor);
    this.doc.rect(x, y, width, 3, 'F');
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(7);
    this.doc.setTextColor(COLORS.textMuted);
    this.doc.text(kpi.label.toUpperCase(), x + 5, y + 10);
    
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(14);
    this.doc.setTextColor(kpi.color || this.themeColor);
    const valueText = kpi.value != null ? this.formatNumber(kpi.value) : 'N/A';
    this.doc.text(valueText, x + 5, y + 20);
    
    if (kpi.unit && kpi.value != null) {
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(7);
      this.doc.setTextColor(COLORS.textMuted);
      this.doc.text(kpi.unit, x + 5, y + 26);
    }
    
    if (kpi.subtitle) {
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(6);
      this.doc.setTextColor(COLORS.textMuted);
      this.doc.text(kpi.subtitle, x + 5, y + 32);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AI INSIGHTS SECTION
  // ═══════════════════════════════════════════════════════════════════════════

  addInsightsSection(insights = []) {
    this.addPageTitle('AI-Powered Insights');
    
    if (insights.length === 0) {
      insights = [{ category: 'BASELINE', text: 'Data is being collected. Continue monitoring to identify optimization opportunities.', color: this.themeColor }];
    }
    
    // Calculate text width with safety margin for font rendering variance
    // Full width (180) - accent bar (4) - left padding (8) - right padding (8) - safety margin (6) = 154mm
    const textWidth = PAGE.contentWidth - 26;
    const textStartX = PAGE.margin + 4 + 8;
    
    insights.forEach((insight, index) => {
      // Pre-calculate text lines for dynamic height
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8);
      const lines = this.doc.splitTextToSize(insight.text, textWidth);
      // Font size 8pt needs ~4.5mm line height for proper spacing
      const lineHeight = 4.5;
      const cardHeight = Math.max(lines.length * lineHeight + 20, 28);
      
      this.checkPageBreak(cardHeight + 5);
      
      const y = this.currentY;
      
      this.doc.setFillColor(index % 2 === 0 ? '#F0FDF4' : '#FEF3C7');
      this.doc.setDrawColor(insight.color || this.themeColor);
      this.doc.setLineWidth(0.5);
      this.doc.roundedRect(PAGE.margin, y, PAGE.contentWidth, cardHeight, 2, 2, 'FD');
      
      this.doc.setFillColor(insight.color || this.themeColor);
      this.doc.rect(PAGE.margin, y, 4, cardHeight, 'F');
      
      // Category label - consistent font
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(9);
      this.doc.setTextColor(insight.color || this.themeColor);
      this.doc.text(insight.category || 'INSIGHT', textStartX, y + 8);
      
      // Insight text - render line by line for consistent spacing
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8);
      this.doc.setTextColor(COLORS.text);
      
      let textY = y + 15;
      lines.forEach((line) => {
        this.doc.text(line, textStartX, textY);
        textY += lineHeight;
      });
      
      this.currentY += cardHeight + 5;
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // IMPROVEMENT OPPORTUNITIES SECTION
  // ═══════════════════════════════════════════════════════════════════════════

  addImprovementsSection(improvements = []) {
    this.addPageTitle('Improvement Opportunities');
    
    if (improvements.length === 0) {
      improvements = [
        { priority: 'Medium', action: 'Continue monitoring and data collection', impact: 'Medium', timeline: 'Ongoing' },
      ];
    }
    
    const colWidths = [25, 90, 30, 35];
    const tableX = PAGE.margin;
    let tableY = this.currentY;
    
    this.doc.setFillColor(this.themeColor);
    this.doc.rect(tableX, tableY, PAGE.contentWidth, 10, 'F');
    
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(8);
    this.doc.setTextColor('#FFFFFF');
    this.doc.text('Priority', tableX + 3, tableY + 7);
    this.doc.text('Action', tableX + colWidths[0] + 3, tableY + 7);
    this.doc.text('Impact', tableX + colWidths[0] + colWidths[1] + 3, tableY + 7);
    this.doc.text('Timeline', tableX + colWidths[0] + colWidths[1] + colWidths[2] + 3, tableY + 7);
    
    tableY += 10;
    
    const priorityColors = { 'High': COLORS.declined, 'Medium': COLORS.attention, 'Low': COLORS.water };
    
    improvements.forEach((imp, index) => {
      const rowHeight = 12;
      
      this.doc.setFillColor(index % 2 === 0 ? COLORS.backgroundAlt : '#FFFFFF');
      this.doc.rect(tableX, tableY, PAGE.contentWidth, rowHeight, 'F');
      
      this.doc.setFillColor(priorityColors[imp.priority] || COLORS.textMuted);
      this.doc.roundedRect(tableX + 3, tableY + 2, 18, 8, 2, 2, 'F');
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(6);
      this.doc.setTextColor('#FFFFFF');
      this.doc.text(imp.priority, tableX + 12, tableY + 7, { align: 'center' });
      
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8);
      this.doc.setTextColor(COLORS.text);
      this.doc.text(imp.action, tableX + colWidths[0] + 3, tableY + 8);
      this.doc.text(imp.impact, tableX + colWidths[0] + colWidths[1] + 3, tableY + 8);
      this.doc.text(imp.timeline, tableX + colWidths[0] + colWidths[1] + colWidths[2] + 3, tableY + 8);
      
      tableY += rowHeight;
    });
    
    this.doc.setDrawColor(COLORS.border);
    this.doc.setLineWidth(0.3);
    this.doc.rect(tableX, this.currentY, PAGE.contentWidth, tableY - this.currentY);
    
    this.currentY = tableY + 10;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // APPENDIX
  // ═══════════════════════════════════════════════════════════════════════════

  addAppendix(definitions = []) {
    this.addPageTitle('Appendix');
    
    // Methodology
    this.addSubsectionTitle('Data Methodology');
    
    const methodology = 'KPIs are calculated using data available in SustainRepo based on the selected reporting period. Metrics are aggregated from facility-level data and follow industry standards including GHG Protocol, GRI, and BRSR frameworks.';
    const methLines = this.doc.splitTextToSize(methodology, PAGE.contentWidth);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    this.doc.setTextColor(COLORS.text);
    this.doc.text(methLines, PAGE.margin, this.currentY);
    this.currentY += methLines.length * 4 + 10;
    
    // Report Metadata
    this.addSubsectionTitle('Report Metadata');
    
    const metadataTable = [
      ['Parameter', 'Value'],
      ['Organization', this.organization.name || 'Not Specified'],
      ['Reporting Period', this.getReportingPeriod()],
      ['Report Type', this.reportTitle],
      ['Report Version', this.reportVersion],
      ['Generated On', this.generatedDate],
      ['Facilities', this.facilities.length > 0 ? `${this.facilities.length} facilities` : 'All facilities'],
    ];
    
    this.addFullWidthTable(metadataTable);
    
    // Definitions
    if (definitions.length > 0) {
      this.currentY += 5;
      this.addSubsectionTitle('Metric Definitions');
      
      definitions.forEach((d) => {
        this.checkPageBreak(15);
        
        this.doc.setFont('helvetica', 'bold');
        this.doc.setFontSize(9);
        this.doc.setTextColor(this.themeColor);
        this.doc.text(d.term, PAGE.margin, this.currentY);
        
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(COLORS.text);
        const defLines = this.doc.splitTextToSize(d.def, PAGE.contentWidth - 5);
        this.doc.text(defLines, PAGE.margin, this.currentY + 4);
        this.currentY += 4 + defLines.length * 4 + 4;
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  formatNumber(value) {
    if (value == null || isNaN(value)) return 'N/A';
    const rounded = Math.round(value * 100) / 100;
    return rounded.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  // Finalize document - add footer to last page
  finalize() {
    this.addFooter();
    this.totalPages = this.pageNumber;
  }

  save(filename) {
    this.finalize();
    this.doc.save(filename || `${this.reportTitle.replace(/\s+/g, '_')}_Report.pdf`);
  }

  getBlob() {
    this.finalize();
    return this.doc.output('blob');
  }
}

export default BasePDFGenerator;
